import { useReducer, useState, useCallback } from 'react';
import keywordFormReducer from 'containers/pages/keywordManagement/reducers/keywordFormReducer';
import { initKeyword, initKeywordValid, loadedKeywordValid } from 'containers/pages/keywordManagement/data';
import { handleKeywordField } from 'containers/pages/keywordManagement/libs';
import { KeywordFormData, KeywordValidation } from 'containers/pages/keywordManagement/types';
import { Keyword } from 'containers/pages/keywordManagement/types';

interface UseKeywordFormProps {
  mode: 'add' | 'edit';
  keyword?: Keyword | null;
  onSave?: (formData: KeywordFormData, isEdit: boolean) => Promise<void>;
}

function useKeywordForm({ mode, keyword, onSave }: UseKeywordFormProps) {
  const getInitialForm = (): KeywordFormData => {
    if (mode === 'edit' && keyword) {
      return { Id: keyword.Id, Title: keyword.Title, Instructions: keyword.Instructions || '' };
    }
    return { ...initKeyword };
  };

  const [formState, dispatch] = useReducer(keywordFormReducer, getInitialForm());
  const [valid, setValid] = useState<KeywordValidation>(
    mode === 'edit' ? { ...loadedKeywordValid } : { ...initKeywordValid },
  );

  const onForm = useCallback(
    (e: { target: { name: string; value: string } }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleKeywordField(e, dispatch, setValid as any);
    },
    [],
  );

  const canNotSave = Object.values(valid).some((v) => v.canNotSave);

  const onSaveForm = useCallback(async () => {
    const current = formState as KeywordFormData;
    const newValid = { ...valid };
    let hasErrors = false;

    if (!current.Title?.trim()) {
      newValid.Title = { isErr: true, errMsg: 'Keyword is required', canNotSave: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setValid(newValid);
      return;
    }

    if (onSave) {
      await onSave(current, mode === 'edit');
    }
  }, [formState, valid, mode, onSave]);

  const onReset = useCallback(() => {
    if (mode === 'add') {
      dispatch({ type: 'RESET', payload: { ...initKeyword } });
      setValid({ ...initKeywordValid });
    }
    if (mode === 'edit' && keyword) {
      dispatch({
        type: 'RESET',
        payload: { Id: keyword.Id, Title: keyword.Title, Instructions: keyword.Instructions || '' },
      });
      setValid({ ...loadedKeywordValid });
    }
  }, [mode, keyword]);

  const state = {
    keyword: formState,
    valid,
    canNotSave,
  };

  const handler = {
    onForm,
    onSave: onSaveForm,
    onReset,
  };

  return { state, handler };
}

export default useKeywordForm;
